'use client';

import ClickAwayListener from '@mui/material/ClickAwayListener';
import debounce from 'common/utils/debounce';
import ProgressRing from 'components/common/ProgressRing';
import RoundIcon from 'components/common/RoundIcon';
import TextEditor, { TextEditorRef } from 'components/common/TextEditor';
import { Button } from 'components/v2/atoms/button';
import PolicyText from 'components/v2/modules/forum/policy-text';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import {
  BlackListUrl,
  checkPolicyCommentText,
  PolicyError,
  PolicyLevel,
} from 'service/post';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

interface ForumCommentBoxPropsType {
  onSubmit: (text: string, parentId?: number) => Promise<void>;
  parentId?: number;
  placeholder?: string;
  onSubscriptionRequired?: () => void;
  supportInlineMode?: boolean;
}

const ForumCommentBox = ({
  onSubmit,
  parentId,
  placeholder = '掲示板にコメントする',
  onSubscriptionRequired,
  supportInlineMode = true,
}: ForumCommentBoxPropsType) => {
  const charLimit = 2000;
  const currentUser = useStore((state) => state.currentUser);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );

  const [isProcessing, setIsProcessing] = useState(false);
  const [textPolicies, setTextPolicies] = useState<PolicyError[]>([]);
  const [blackUrls, setBlackUrls] = useState<BlackListUrl[]>([]);
  const [postable, setPostable] = useState(false);
  const [plainText, setPlainText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const textEditorRef = useRef<TextEditorRef>();

  const usingTextPolices = useMemo(() => {
    return textPolicies.filter((po) => po.level === PolicyLevel.SuperHigh);
  }, [textPolicies]);
  const hasPolicyError = usingTextPolices.length > 0 || blackUrls.length > 0;

  const textLength = useMemo(() => plainText.length, [plainText]);

  const isInlineMode = supportInlineMode && !isFocused && textLength === 0;

  useEffect(() => {
    setPostable(textLength > 0 && textLength <= charLimit);
  }, [textLength, charLimit]);

  const { mutate: checkPostText } = useMutation(
    async (text: string) => {
      return checkPolicyCommentText(text);
    },
    {
      onSuccess: (data) => {
        const policies = data?.data?.words || [];
        const isSame =
          policies.length === textPolicies.length &&
          policies.every((pl) =>
            textPolicies.some((policy) => policy.name === pl.name),
          );
        if (!isSame) setTextPolicies(policies);
        const urls = data?.data?.urls || [];
        const isSameUrls =
          urls.length === blackUrls.length &&
          urls.every((blUrl) => blackUrls.some((url) => url.url === blUrl.url));
        if (!isSameUrls) setBlackUrls(urls);
        setIsProcessing(false);
      },
      onError: () => {
        setIsProcessing(false);
      },
    },
  );

  const debouncedCheckPostText = useMemo(
    () => debounce(checkPostText, 1000),
    [checkPostText],
  );

  useEffect(() => {
    setIsProcessing(true);
    debouncedCheckPostText(plainText);
  }, [plainText, debouncedCheckPostText]);

  useEffect(() => {
    if (!textEditorRef.current) return;
    textEditorRef.current.forceRender();
  }, [textPolicies, blackUrls]);

  const handleTextAreaFocus = useCallback(() => setIsFocused(true), []);
  const handleClickAway = useCallback(() => setIsFocused(false), []);

  const handleSubmit = useCallback(async () => {
    if (!currentUser) {
      showLogin({});
      return;
    }

    // Check subscription requirement
    if (onSubscriptionRequired) {
      onSubscriptionRequired();
      return;
    }

    const content = textEditorRef.current?.getContent() || '';
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(content.trim(), parentId);
      textEditorRef.current?.reset();
      setPlainText('');
    } catch {
      setSnackbarMessage({ type: 'error', text: 'コメントに失敗しました' });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentUser,
    showLogin,
    onSubmit,
    parentId,
    setSnackbarMessage,
    onSubscriptionRequired,
  ]);

  return (
    <div className="flex w-full gap-3">
      <div className="shrink-0">
        <RoundIcon user={currentUser} width={35} height={35} hasRing />
      </div>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div
          className={`flex-1 overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all duration-200 ${
            isInlineMode ? 'py-2' : 'py-3'
          }`}
        >
          <div className="px-3">
            <TextEditor
              ref={textEditorRef}
              setPlainText={setPlainText}
              placeholder={placeholder}
              onFocus={handleTextAreaFocus}
              blackUrls={blackUrls}
              readOnly={false}
              textPolicies={usingTextPolices}
              charLimit={charLimit}
            />
          </div>

          <div
            className={`flex items-center justify-end gap-2 px-3 transition-all duration-200 ${
              isInlineMode
                ? 'mt-0 max-h-0 overflow-hidden opacity-0'
                : 'mt-2 max-h-20 opacity-100'
            }`}
          >
            <ProgressRing radius={22} current={textLength} total={charLimit} />
            <Button
              variant="defaultGreen"
              size="sm"
              disabled={!postable || isProcessing || hasPolicyError}
              onClick={() => void handleSubmit()}
              className="min-w-[60px] rounded-full"
            >
              {isSubmitting ? '投稿中...' : '投稿'}
            </Button>
          </div>

          {hasPolicyError && (
            <div className="mt-2 px-3 text-xs text-red-500">
              投稿ポリシーに違反している可能性があります。
            </div>
          )}

          <div
            className={`transition-all duration-200 ${
              isInlineMode ? 'mt-0 max-h-0 overflow-hidden opacity-0' : 'mt-3'
            }`}
          >
            <PolicyText className="px-3" />
          </div>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default ForumCommentBox;
